import { Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import { MAX_CATEGORIES, MAX_SUBCATEGORIES } from "../../selector/sector-draft";
import type {
  SectorDraft,
  SectorSource,
  SlotIndex,
} from "../../selector/types";

/**
 * The pieces tiles, rows and sections share. The sign of a suggested item is
 * one only: a dashed border and a sparkle. Colors move over 150 ms
 * `--ease-out-strong` and nothing else animates (motion budget M4).
 *
 * Text colors: never `text-primary` (`#1e77eb`, 4.29:1) for words; the text
 * color or the ring blue (`--ring`, 6.47:1) instead (AC28).
 */

type Translate = ReturnType<typeof useI18n>["t"];

const I18N = "taxonomy.sector" as const;

/** Color change of tiles, rows and ticks: 150 ms, never `duration-150` (M4). */
export const COLOR_TRANSITION =
  "transition-colors [transition-duration:150ms] ease-out-strong";

/**
 * The focus ring of the house controls (`--ring`, 1.5 px) drawn **inside** the
 * control: the morphing shell has `overflow: hidden` and would clip an outer
 * ring (AC30).
 */
export const FOCUS_RING =
  "focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-inset focus-visible:ring-ring";

/** The sparkle that marks a suggested item. Decorative: the name says it. */
export const SuggestedMark = (props: { class?: string }) => (
  <i
    class={cn("ph-fill ph-sparkle text-[0.8em] shrink-0", props.class)}
    aria-hidden="true"
  />
);

/** The tail of the accessible name of a suggested row: «, suggerito da Jev». */
export function originSuffix(t: Translate, source: SectorSource): string {
  return t(`${I18N}.origin.${source ?? "jev"}`);
}

/** «n/2» next to the title of the grid. Visible text, no `sr-only` prefix (AC27). */
export const CategoryCounter = (props: {
  draft: SectorDraft;
  class?: string;
}) => (
  <span class={cn("text-xs tabular-nums text-foreground/70", props.class)}>
    {props.draft.chosenCategories().length}/{MAX_CATEGORIES}
  </span>
);

/**
 * «n/3», amber when the sector is full. `amber-800` is 6.56:1 on the `#f6f6f6`
 * page and 7.09:1 on white; the quiet tone is the text color at 70%.
 */
export const SubCounter = (props: { count: number; class?: string }) => (
  <span
    class={cn(
      "text-xs tabular-nums",
      props.count >= MAX_SUBCATEGORIES
        ? "text-amber-800"
        : "text-foreground/70",
      props.class,
    )}
  >
    {props.count}/{MAX_SUBCATEGORIES}
  </span>
);

/**
 * «Principale» / «Secondario». The primary is filled with the ring blue, which
 * holds white at 6.47:1 (`bg-primary` is 4.29:1: AC28).
 */
export const SlotBadge = (props: { index: SlotIndex; class?: string }) => {
  const { t } = useI18n();
  return (
    <span
      class={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4",
        props.index === 0
          ? "bg-ring text-white"
          : "bg-secondary text-secondary-foreground",
        props.class,
      )}
    >
      {props.index === 0
        ? t(`${I18N}.badge.primary`)
        : t(`${I18N}.badge.secondary`)}
    </span>
  );
};

/** The house icon button: 24 px, the focus ring always shows. */
export const IconButton = (props: {
  label: string;
  icon: string;
  onClick: () => void;
  class?: string;
}) => (
  <button
    type="button"
    aria-label={props.label}
    title={props.label}
    onClick={() => props.onClick()}
    class={cn(
      "flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-foreground/70 hover:bg-foreground/5 hover:text-foreground",
      COLOR_TRANSITION,
      FOCUS_RING,
      props.class,
    )}
  >
    <i class={cn("ph", props.icon)} aria-hidden="true" />
  </button>
);

/**
 * «Al massimo 3: togline uno per sceglierne un altro.», for screen readers
 * only: on screen the counter and the modal description give the cap. Always
 * mounted so a list can point `aria-describedby` at it; the text appears at
 * 3/3 and the live region announces it once.
 */
export const FullHint = (props: {
  id?: string;
  show: boolean;
  class?: string;
}) => {
  const { t } = useI18n();
  return (
    <p id={props.id} aria-live="polite" class={cn("sr-only", props.class)}>
      <Show when={props.show}>{t(`${I18N}.subcategories.limit`)}</Show>
    </p>
  );
};
